import { useState } from "react";
import ReactMarkdown from "react-markdown";
import UploadScreen from "../components/UploadScreen";
import ContextForm from "../components/ContextForm";
import UrgencyBadge from "../components/UrgencyBadge";
import { analyzeImage } from "../services/gemini";

export default function AnalyzePage() {
  const [imageFile, setImageFile] = useState(null);
  const [age, setAge] = useState("");
  const [scanType, setScanType] = useState("");
  const [symptoms, setSymptoms] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [analysisTime, setAnalysisTime] = useState(null);

  const canAnalyze = !!imageFile && !loading;

  async function handleAnalyze() {
    if (!canAnalyze) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const text = await analyzeImage({ imageFile, age, scanType, symptoms });
      setResult(text);
      setAnalysisTime(new Date().toLocaleString());
    } catch (err) {
      setError(err.message || "Something went wrong. Please check your API key and try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setImageFile(null);
    setAge("");
    setScanType("");
    setSymptoms("");
    setResult(null);
    setError(null);
    setLoading(false);
    setAnalysisTime(null);
  }

  /* ── Make phone numbers in markdown clickable ── */
  function linkifyPhones(text) {
    return text.replace(
      /(\+?[\d\s-]{7,})/g,
      (match) => {
        const digits = match.replace(/\D/g, "");
        if (digits.length < 7) return match;
        return `[${match}](tel:${digits})`;
      }
    );
  }

  const processedResult = result ? linkifyPhones(result) : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "40px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* Back link */}
        <a
          href="/"
          style={{
            color: "#2563eb", fontSize: 14, fontWeight: 500,
            display: "block", marginBottom: 24, textDecoration: "none",
          }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
          onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
        >
          ← RepoScan
        </a>

        {result ? (
          /* ── Results card ── */
          <div style={{
            background: "#fff",
            borderRadius: 24,
            boxShadow: "0 4px 32px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}>
            {/* Report header banner */}
            <div style={{
              background: "#eff6ff",
              borderBottom: "2px solid #dbeafe",
              padding: "16px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img src="/assets/logo.svg" alt="RepoScan" style={{ height: 24, width: "auto" }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1e40af" }}>
                  Clinical Report
                </span>
              </div>
              {analysisTime && (
                <span style={{ fontSize: 12, color: "#64748b" }}>{analysisTime}</span>
              )}
            </div>

            {/* Title + urgency badge */}
            <div style={{
              display: "flex", flexWrap: "wrap",
              justifyContent: "space-between", alignItems: "flex-start",
              gap: 12, padding: "24px 36px 0",
            }}>
              <h2 style={{ fontWeight: 700, fontSize: 22, color: "#0f172a", margin: 0 }}>
                Scan Analysis
              </h2>
              <UrgencyBadge resultText={result} large />
            </div>

            {/* Markdown result */}
            <div style={{ color: "#374151", lineHeight: 1.7, padding: "16px 36px 0" }}>
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <h2 style={{
                      fontSize: 18, fontWeight: 700, color: "#0f172a",
                      marginTop: 28, marginBottom: 12,
                      borderBottom: "2px solid #f1f5f9", paddingBottom: 8,
                    }}>
                      {children}
                    </h2>
                  ),
                  p: ({ children }) => (
                    <p style={{ marginBottom: 12, color: "#374151" }}>{children}</p>
                  ),
                  ol: ({ children }) => (
                    <ol style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ol>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li style={{ color: "#475569", marginBottom: 4, lineHeight: 1.8 }}>{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: 600, color: "#0f172a" }}>{children}</strong>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>
                      {children}
                    </a>
                  ),
                  hr: () => (
                    <hr style={{ border: "none", borderTop: "1px solid #f1f5f9", margin: "24px 0" }} />
                  ),
                  blockquote: ({ children }) => (
                    <div style={{
                      background: "#eff6ff",
                      borderLeft: "4px solid #2563eb",
                      borderRadius: 12, padding: 20,
                      margin: "16px 0",
                    }}>
                      {children}
                    </div>
                  ),
                }}
              >
                {processedResult}
              </ReactMarkdown>
            </div>

            {/* Bottom actions */}
            <div style={{ padding: "0 36px 36px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <button
                onClick={handleReset}
                style={{
                  background: "none", border: "none",
                  color: "#2563eb", fontSize: 14, fontWeight: 500,
                  cursor: "pointer", padding: 0,
                }}
              >
                ← Analyze another scan
              </button>
              <button
                onClick={() => window.print()}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12, padding: "10px 24px",
                  fontSize: 14, color: "#4b5563",
                  background: "#fff", cursor: "pointer",
                }}
              >
                🖨️ Save Report
              </button>
            </div>
          </div>
        ) : (
          /* ── Upload card ── */
          <div style={{
            background: "#fff",
            borderRadius: 24,
            boxShadow: "0 4px 32px rgba(0,0,0,0.08)",
            padding: 36,
          }}>
            <h2 style={{ fontWeight: 700, fontSize: 20, color: "#0f172a", marginBottom: 4 }}>
              Upload your scan
            </h2>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>
              Upload an X-ray, MRI, or skin condition photo. We'll analyze it and
              explain what it shows in plain English.
            </p>

            <UploadScreen imageFile={imageFile} setImageFile={setImageFile} />

            <ContextForm
              age={age} setAge={setAge}
              scanType={scanType} setScanType={setScanType}
              symptoms={symptoms} setSymptoms={setSymptoms}
            />

            {error && (
              <div style={{
                marginTop: 16,
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: 12, padding: "12px 16px",
              }}>
                <p style={{ fontSize: 14, color: "#991b1b", margin: 0 }}>{error}</p>
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              style={{
                marginTop: 24, width: "100%",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "14px 24px",
                borderRadius: 16,
                border: "none",
                fontSize: 16, fontWeight: 600,
                cursor: canAnalyze ? "pointer" : "not-allowed",
                background: canAnalyze ? "#2563eb" : "#d1d5db",
                color: "#fff",
                boxShadow: canAnalyze ? "0 4px 20px rgba(37,99,235,0.25)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              {loading ? (
                <>
                  <svg
                    style={{ animation: "spin 1s linear infinite", width: 20, height: 20 }}
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                  >
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" opacity="0.25" />
                    <path fill="white" opacity="0.75"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing your scan…
                </>
              ) : (
                "🔍 Analyze Scan"
              )}
            </button>

            {!imageFile && (
              <p style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 10 }}>
                Upload a scan image to enable analysis
              </p>
            )}
          </div>
        )}

        <p style={{
          textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 32,
        }}>
          RepoScan · Not a substitute for professional medical advice
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
